import type { RouterOutputs } from './trpc';

/** One row from `search.list` — a contact or a task. Discriminated on `entityType`. */
export type SearchResult = RouterOutputs['search']['list']['items'][number];

export type SearchResultOf<T extends SearchResult['entityType']> = Extract<
  SearchResult,
  { entityType: T }
>;

/**
 * The secondary line under a search result's title.
 *
 * The API returns structured fields rather than a pre-composed string, so this is the one
 * place that decides how they read. Empty segments are dropped rather than leaving stray
 * separators, and `null` means "render no subtitle at all" rather than an empty row.
 */
export function searchResultSubtitle(result: SearchResult): string | null {
  const parts =
    result.entityType === 'contact'
      ? [result.jobTitle, result.companyName]
      : [result.statusLabel ?? result.status, result.contact?.label];

  const joined = parts.filter((part): part is string => !!part?.trim()).join(' · ');
  return joined || null;
}

/**
 * Every piece of text the server could have matched this result on.
 *
 * The palette disables cmdk's own filtering for remote results (see `CommandPalette`), but
 * cmdk still needs a `value` per item for keyboard selection. Mirroring the server's indexed
 * fields here keeps that value meaningful — and documents, in one place, that a task can match
 * on its linked contact's name without saying so in its title.
 */
export function searchResultValue(result: SearchResult): string {
  const parts =
    result.entityType === 'contact'
      ? [result.title, result.jobTitle, result.companyName]
      : [result.title, result.contact?.label];

  return `${result.entityType}:${result.id} ${parts.filter(Boolean).join(' ')}`;
}

/**
 * Case-insensitive all-tokens-present match, used for the palette's *local* groups (recent
 * history and navigation destinations) now that cmdk's built-in filter is switched off.
 *
 * Filtering locally and remotely with one predicate would be wrong — the server's FTS applies
 * porter stemming, so it legitimately matches "renewals" for "renewal" where a substring test
 * would not. Local rows have no such index, and a plain substring test is what users expect of
 * a short, visible list.
 */
export function matchesLocalQuery(haystack: (string | null | undefined)[], query: string): boolean {
  const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;

  const text = haystack.filter(Boolean).join(' ').toLowerCase();
  return tokens.every((token) => text.includes(token));
}
